<script setup lang="ts">
import useBackend from '@/composition/useBackend';
import {LegacyOrder, legacyOrderKey, useLocalization} from '@hantera/portal-app';
import {TabBar, TabItem} from '@hantera/design-system';
import {computed, inject, ref, watch} from 'vue';
import CompactOrder from '@/components/LegacyCompactOrder/LegacyCompactOrder.vue';

const emit = defineEmits<{
  (e: 'openOrder', order: LegacyOrder): void
}>();

const order = inject(legacyOrderKey, ref<LegacyOrder>(<any>{}));
const backend = useBackend();
const t = useLocalization();

const selectedTab = ref<string>('details');

const orders = ref<LegacyOrder[]>([]);

watch(() => order.value.customer.email, async email => {
  orders.value = await backend.legacyCustomers.getCustomerOrders(email);
}, { immediate: true });

const orderCount = computed(() => orders.value?.length || 0);

function openOrder(customerOrder: LegacyOrder) {
  if (order.value.orderId === customerOrder.orderId) {
    return;
  }

  emit('openOrder', customerOrder);
}

</script>

<template>
  <div>
    <div class="box-content-label">
      Customer
    </div>
    <div
      class="order-customer box-content box-pop"
    >
      <TabBar v-model="selectedTab">
        <TabItem
          value="details"
          :label="t('orderView.customer.details')"
        />
        <TabItem
          value="orders"
          :label="t('orderView.customer.orders')"
          :count="orderCount"
        />
      </TabBar>
      <div
        v-if="selectedTab === 'details'"
        class="order-customer__details"
      >
        <div class="order-customer__row">
          <div class="order-customer__details-field-name">
            Number
          </div>
          <div class="order-customer__details-field-value">
            {{ order.customer.customerNumber }}
          </div>
        </div>
        <div
          class="order-customer__row"
          v-if="order.customer.customerName"
        >
          <div class="order-customer__details-field-name">
            Name
          </div>
          <div class="order-customer__details-field-value">
            {{ order.customer.customerName }}
          </div>
        </div>
        <div
          class="order-customer__row"
          v-if="order.customer.email"
        >
          <div class="order-customer__details-field-name">
            E-mail
          </div>
          <div class="order-customer__details-field-value">
            {{ order.customer.email }}
          </div>
        </div>
        <div
          class="order-customer__row"
          v-if="order.customer.phoneNumber"
        >
          <div class="order-customer__details-field-name">
            Phone
          </div>
          <div class="order-customer__details-field-value">
            {{ order.customer.phoneNumber }}
          </div>
        </div>
      </div>
      <div v-if="selectedTab === 'orders'">
        <CompactOrder
          v-for="customerOrder in orders"
          :key="customerOrder.orderId"
          v-on="order.orderId !== customerOrder.orderId ? { click: openOrder } : {}"
          :order="customerOrder"
        />
      </div>
    </div>

  </div>
</template>

<style>
.order-customer__details {
  display: grid;
  grid-auto-flow: row;
}

.order-customer__row {
  border-bottom: 1px solid hsl(var(--color-70));
  padding: 5px;
}

.order-customer__row:last-child {
  border-bottom: none;
}
</style>